import React, { useState, useEffect } from 'react';
import { fetchNewVsReturningAP } from '../services/api';

const now = new Date();
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const money = n => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const intFmt = n => Number(n || 0).toLocaleString('en-US');

export default function NewVsReturningAP() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const load = () => {
    setLoading(true);
    setError(null);
    fetchNewVsReturningAP(month, year)
      .then(r => setRows(Array.isArray(r) ? r : (r?.data ?? [])))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const tot = rows.reduce((a, r) => ({
    orders: a.orders + Number(r.orderCount || 0),
    rev: a.rev + Number(r.totalSum || 0),
    newc: a.newc + Number(r.newCustomers || 0),
    newvip: a.newvip + Number(r.newVipCustomers || 0),
    retc: a.retc + Number(r.returningCustomers || 0),
    retvip: a.retvip + Number(r.returningVipCustomers || 0),
  }), { orders: 0, rev: 0, newc: 0, newvip: 0, retc: 0, retvip: 0 });

  const years = [];
  for (let y = now.getFullYear(); y >= 2019; y--) years.push(y);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>New vs Returning (AP)</h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
          Customer counts by day, split New/Returning and VIP. "New" = order placed the day the account was created. Matches the legacy Admin Portal report.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Period</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>MONTH</div>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} className="form-select" style={{ fontSize: 11 }}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>YEAR</div>
            <select value={year} onChange={e => setYear(Number(e.target.value))} className="form-select" style={{ fontSize: 11 }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <button onClick={load} className="btn btn-primary" style={{ fontSize: 11 }}>Run Report</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Orders', value: intFmt(tot.orders) },
            { label: 'New', value: intFmt(tot.newc) + (tot.newvip ? ` (${intFmt(tot.newvip)} VIP)` : ''), highlight: true },
            { label: 'Returning', value: intFmt(tot.retc) + (tot.retvip ? ` (${intFmt(tot.retvip)} VIP)` : '') },
            { label: 'Revenue', value: money(tot.rev) },
          ].map(({ label, value, highlight }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: highlight ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">By Day {rows.length > 0 && `(${rows.length} days)`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No data found for selected period.</div>
        )}
        {rows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>New</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>New VIP</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Returning</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ret. VIP</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--navy)', fontWeight: 600 }}>{r.date ? new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(r.orderCount)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 600 }}>{intFmt(r.newCustomers)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{intFmt(r.newVipCustomers)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(r.returningCustomers)}</td>
                    <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{intFmt(r.returningVipCustomers)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-700)' }}>{money(r.totalSum)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--gray-300)', fontWeight: 700, background: 'var(--peach)' }}>
                  <td style={{ padding: '8px 12px', color: 'var(--navy)' }}>Total</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(tot.orders)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', color: 'var(--orange)' }}>{intFmt(tot.newc)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(tot.newvip)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(tot.retc)}</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right' }}>{intFmt(tot.retvip)}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>{money(tot.rev)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
